import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, BarChart3, UserCog, FileText, Bell, ChevronDown, Users, CheckCircle, XCircle, AlertTriangle, UploadCloud, BookOpen, Map, ArrowLeft, Loader2 } from 'lucide-react';
// Mock Data
const importedLearners = [{
  id: 1,
  name: 'Sarah Johnson',
  email: 'sarah.j@example.com',
  status: 'success',
  message: 'Ready to import'
}, {
  id: 2,
  name: 'Michael Chen',
  email: 'mchen@example.com',
  status: 'success',
  message: 'Ready to import'
}, {
  id: 3,
  name: 'Emily Davis',
  email: 'emily.d@example.com',
  status: 'success',
  message: 'Ready to import'
}, {
  id: 4,
  name: 'John Smith',
  email: 'john.smith@example.com',
  status: 'warning',
  message: 'Account already exists, will update'
}, {
  id: 5,
  name: 'Lisa Wong',
  email: 'lisa.w@example.com',
  status: 'success',
  message: 'Ready to import'
}, {
  id: 6,
  name: 'Alex Rivera',
  email: 'alex.r@example.com',
  status: 'success',
  message: 'Ready to import'
}, {
  id: 7,
  name: 'David Kim',
  email: 'david.k@example.com',
  status: 'error',
  message: 'Invalid email format'
}, {
  id: 8,
  name: 'Rachel Green',
  email: 'rachel.g@example.com',
  status: 'success',
  message: 'Ready to import'
}];
const availableSkills = ['AI Functionality Beyond the Hype', 'Active and Empathetic Listening', 'DEI: Employee Relations', 'Developing EQ and Empathy', 'EQ for the Workplace', 'Effective Communication'];
const availablePathways = ['Leadership Essentials', 'Technical Foundations', 'Communication Mastery'];
export function BulkUploadResultsPage() {
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  // Assignment State
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [selectedPathways, setSelectedPathways] = useState<string[]>([]);
  const [enableAllSkills, setEnableAllSkills] = useState(false);
  const [enableAllPathways, setEnableAllPathways] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);
  // Sync "Enable All" toggles with individual selections
  useEffect(() => {
    if (enableAllSkills) {
      setSelectedSkills(availableSkills);
    } else if (selectedSkills.length === availableSkills.length) {
      setSelectedSkills([]);
    }
  }, [enableAllSkills]);
  useEffect(() => {
    if (selectedSkills.length === availableSkills.length && availableSkills.length > 0) {
      setEnableAllSkills(true);
    } else if (selectedSkills.length < availableSkills.length) {
      setEnableAllSkills(false);
    }
  }, [selectedSkills]);
  useEffect(() => {
    if (enableAllPathways) {
      setSelectedPathways(availablePathways);
    } else if (selectedPathways.length === availablePathways.length) {
      setSelectedPathways([]);
    }
  }, [enableAllPathways]);
  useEffect(() => {
    if (selectedPathways.length === availablePathways.length && availablePathways.length > 0) {
      setEnableAllPathways(true);
    } else if (selectedPathways.length < availablePathways.length) {
      setEnableAllPathways(false);
    }
  }, [selectedPathways]);
  const toggleSkill = (skill: string) => {
    setSelectedSkills(prev => prev.includes(skill) ? prev.filter(s => s !== skill) : [...prev, skill]);
  };
  const togglePathway = (pathway: string) => {
    setSelectedPathways(prev => prev.includes(pathway) ? prev.filter(p => p !== pathway) : [...prev, pathway]);
  };
  const handleConfirm = () => {
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      navigate('/assessment'); // Navigate back to users page
    }, 1500);
  };
  const stats = [{
    label: 'Total Processed',
    value: '124',
    icon: Users,
    color: 'text-[#30718D]',
    bg: 'bg-[#30718D]/10'
  }, {
    label: 'Ready to Import',
    value: '120',
    icon: CheckCircle,
    color: 'text-[#007558]',
    bg: 'bg-[#007558]/10'
  }, {
    label: 'Needs Attention',
    value: '4',
    icon: AlertTriangle,
    color: 'text-yellow-600',
    bg: 'bg-yellow-100'
  }];
  return <div className="flex-1 bg-gray-50 min-h-screen pb-12" data-id="element-2374" data-mp="1kqhoex0r6c89">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30" data-id="element-2375" data-mp="1ksu5z92lmkks">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2376" data-mp="1yqir8i0kvtqd">
          <div className="flex items-center h-16" data-id="element-2377" data-mp="1ts7f20mt974z">
            {/* Logo */}
            <div className="text-[#DB3725] font-bold text-xl" data-id="element-2378" data-mp="2dcg4hzn4hlj5">LOGO</div>

            {/* Navigation Links */}
            <nav className="flex items-center gap-4 sm:gap-8 ml-6 sm:ml-12" data-id="element-2379" data-mp="2icavs4at8d4q">
              <Link data-phoebe-nav="/admin" to="/admin" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2380" data-mp="24cjk4a7rn8tw">
                <Home size={18} data-id="element-2381" data-mp="28fy1p5bcrx9g" />
                <span className="hidden sm:inline" data-id="element-2382" data-mp="1umq9bbfg6xqp">Admin Hub</span>
              </Link>
              <Link data-phoebe-nav="/analytics" to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2383" data-mp="1zd6op12twtfp">
                <BarChart3 size={18} data-id="element-2384" data-mp="1km7hnuhn6cy8" />
                <span className="hidden sm:inline" data-id="element-2385" data-mp="1pmu5bc6r36b5">Analytics</span>
              </Link>
              <Link data-phoebe-nav="/feedback" to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2386" data-mp="1bnm64jal7y6y">
                <FileText size={18} data-id="element-2387" data-mp="1gorwiagrz9db" />
                <span className="hidden sm:inline" data-id="element-2388" data-mp="3icebfadjq0bs">Reports</span>
              </Link>
              <Link data-phoebe-nav="/assessment" to="/assessment" className="flex items-center gap-2 text-[#30718D] border-b-2 border-[#30718D] pb-[22px] pt-[22px] transition-colors text-sm font-medium" data-id="element-2389" data-mp="3dl4pajr7hf4n">
                <UserCog size={18} data-id="element-2390" data-mp="34kq2ffylfrz1" />
                <span className="hidden sm:inline" data-id="element-2391" data-mp="34lrbxfzyo6en">Users</span>
              </Link>
            </nav>

            {/* Right Side - User Info */}
            <div className="flex items-center gap-2 sm:gap-4 ml-auto" data-id="element-2392" data-mp="52yrltyxxxbl">
              <Link data-phoebe-nav="/alerts" to="/alerts" aria-label="Notifications" className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-2393" data-mp="71b9nnogqyt">
                <Bell size={20} data-id="element-2394" data-mp="3nbqwpqorqzc9" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-2395" data-mp="3nao7dn73h4yg"></span>
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-2396" data-mp="2gsixhsesyco0">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-2397" data-mp="2buzk0upxi3x4">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-2398" data-mp="wcbevds35y5a">
                    LT
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-900" data-id="element-2399" data-mp="1554yfnekfjoy">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-2400" data-mp="1a3g6bslavk4j" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-2401" data-mp="1f1rhq7r8u2ov">
                    <div className="py-2" data-id="element-2402" data-mp="1k0ksvtj25hlv">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2403" data-mp="1o2zdoj6aerfs">
                        <Users size={18} className="text-gray-600" data-id="element-2404" data-mp="3lw51uddsjy0n" />
                        <span className="font-medium text-gray-900" data-id="element-2405" data-mp="3pvvwwtacp0az">
                          Profile
                        </span>
                      </Link>
                      <div className="border-t border-gray-200 my-2" data-id="element-2406" data-mp="320k52er2slzd"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm text-[#DB3725]" data-id="element-2407" data-mp="3frfim4cirr9a">
                        <span className="font-medium" data-id="element-2408" data-mp="3bqdjdnilrf7b">Logout</span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-id="element-2409" data-mp="2x0xr233fb1pi">
        {/* Page Header */}
        <div data-phoebe-tour="bulk-upload" className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8" data-id="element-2410" data-mp="2s1cv81jjv9h0">
          <div data-id="element-2411" data-mp="c9owq1ow18w3">
            <Link to="/assessment" className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors mb-2" data-id="element-2412" data-mp="c95hisqmgylr">
              <ArrowLeft size={16} data-id="element-2413" data-mp="fs4k573ii2uw" />
              Back to Users
            </Link>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3" data-id="element-2414" data-mp="ju99vuquafc9">
              <UploadCloud className="text-[#30718D]" size={32} data-id="element-2415" data-mp="5uggtsrubn14" />
              Upload Results
            </h1>
            <p className="text-gray-600 mt-1" data-id="element-2416" data-mp="at1f3mnj7wmx">
              Review your imported learners and assign their initial learning
              paths.
            </p>
          </div>
          <div className="flex items-center gap-3 self-start md:self-auto" data-id="element-2417" data-mp="yli0o76ha6qz">
            <button onClick={() => navigate('/assessment')} className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium text-sm" data-id="element-2418" data-mp="13hfbxz2vwmww">
              Cancel
            </button>
            <button onClick={handleConfirm} disabled={isSubmitting} className="flex items-center gap-2 px-6 py-2.5 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium text-sm shadow-sm disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-2419" data-mp="1hhzz17fu14w7">
              {isSubmitting ? <>
                  <Loader2 size={18} className="animate-spin" data-id="element-2420" data-mp="2o5r7qnazr8sl" />
                  Processing...
                </> : <>
                  <CheckCircle size={18} data-id="element-2421" data-mp="2a717iysvkt00" />
                  Confirm & Import
                </>}
            </button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8" data-id="element-2422" data-mp="20gf0vu34zctg">
          {stats.map((stat, index) => {
          const Icon = stat.icon;
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 flex items-center gap-4" data-id="element-2423" data-mp="258pygqaemmiu" data-mpi={index}>
                <div className={`w-12 h-12 rounded-full ${stat.bg} flex items-center justify-center flex-shrink-0`} data-id="element-2424" data-mp="1hjwzsm2ee0kj">
                  <Icon size={24} className={stat.color} data-id="element-2425" data-mp="12uvjiae4jj2o" />
                </div>
                <div data-id="element-2426" data-mp="12wovm57iavi2">
                  <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-2427" data-mp="xw0vmu1av4bp">
                    {stat.label}
                  </div>
                  <div className="text-2xl font-bold text-gray-900" data-id="element-2428" data-mp="1cu2wuxkuzdps">
                    {stat.value}
                  </div>
                </div>
              </motion.div>;
        })}
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-2429" data-mp="1lrwqu1v5hv9s">
          {/* Left Column: Imported Learners Table */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3
        }} className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col h-[600px]" data-id="element-2430" data-mp="1gssifdg1w1ki">
            <div className="p-5 border-b border-gray-200 bg-gray-50/50 flex items-center justify-between" data-id="element-2431" data-mp="20im55d3wrqhv">
              <h2 className="text-lg font-bold text-gray-900" data-id="element-2432" data-mp="27m2f3tvrgly1">
                Import Preview
              </h2>
              <span className="text-sm text-gray-500" data-id="element-2433" data-mp="2ck41anohh1dq">
                Showing {importedLearners.length} of 124
              </span>
            </div>

            <div className="flex-1 overflow-auto" data-id="element-2434" data-mp="2hhx6pehyprcy">
              <table className="w-full text-left border-collapse" data-id="element-2435" data-mp="2mayllmi65qwj">
                <thead className="bg-white sticky top-0 z-10 shadow-sm" data-id="element-2436" data-mp="2qcbts38qi66m">
                  <tr data-id="element-2437" data-mp="2qeeimjy5ht9r">
                    <th className="py-3 px-5 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-2438" data-mp="2vbenwsn6erzt">
                      Learner
                    </th>
                    <th className="py-3 px-5 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 hidden sm:table-cell" data-id="element-2439" data-mp="308n9z4bg3g6c">
                      Email
                    </th>
                    <th className="py-3 px-5 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-2440" data-mp="1avjs4sp65h66">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-2441" data-mp="3jeupolwsa9w3">
                  {importedLearners.map((learner, index) => <motion.tr key={learner.id} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.4 + index * 0.05
                }} className="hover:bg-gray-50 transition-colors" data-id="element-2442" data-mp="3fekmti4afzlg" data-mpi={index}>
                      <td className="py-3 px-5" data-id="element-2443" data-mp="6vlg1kpldg73">
                        <div className="flex items-center gap-3" data-id="element-2444" data-mp="1xto4qoy5u2w">
                          <div className="w-8 h-8 rounded-full bg-[#30718D]/10 flex items-center justify-center text-[#30718D] text-xs font-bold flex-shrink-0" data-id="element-2445" data-mp="3t4whnos9ccga">
                            {learner.name.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div data-id="element-2446" data-mp="2hofybkdl1bo5">
                            <div className="font-medium text-gray-900 text-sm" data-id="element-2447" data-mp="9h2mogz7as6u">
                              {learner.name}
                            </div>
                            <div className="text-xs text-gray-500 sm:hidden" data-id="element-2448" data-mp="e9cek2jvm2br">
                              {learner.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-5 text-sm text-gray-600 hidden sm:table-cell" data-id="element-2449" data-mp="t5bh6xo7va9i">
                        {learner.email}
                      </td>
                      <td className="py-3 px-5" data-id="element-2450" data-mp="x5ubcydl0sev">
                        {learner.status === 'success' && <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#007558]/10 text-[#007558] text-xs font-medium" data-id="element-2451" data-mp="j62qqesk8drw">
                            <CheckCircle size={14} data-id="element-2452" data-mp="33xczqgbo1ghx" />
                            <span data-id="element-2453" data-mp="33viet20e9odu">Ready</span>
                          </div>}
                        {learner.status === 'warning' && <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-100 text-yellow-700 text-xs font-medium" title={learner.message} data-id="element-2454" data-mp="38xhi6bw4wlas">
                            <AlertTriangle size={14} data-id="element-2455" data-mp="2wcsncphjge2t" />
                            <span data-id="element-2456" data-mp="3ad3dtrexrqng">Update</span>
                          </div>}
                        {learner.status === 'error' && <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-100 text-red-700 text-xs font-medium" title={learner.message} data-id="element-2457" data-mp="35eif5wfc1f14">
                            <XCircle size={14} data-id="element-2458" data-mp="2hmblx64wlo5x" />
                            <span data-id="element-2459" data-mp="2do44sa6j2ku7">Error</span>
                          </div>}
                      </td>
                    </motion.tr>)}
                </tbody>
              </table>
            </div>
            <div className="p-4 border-t border-gray-200 bg-gray-50 text-center" data-id="element-2460" data-mp="2mnircyjkqb9f">
              <button className="text-sm font-medium text-[#30718D] hover:text-[#265a70] transition-colors" data-id="element-2461" data-mp="1yxoi0qygr4tx">
                View all 124 records
              </button>
            </div>
          </motion.div>

          {/* Right Column: Assignment Controls */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.4
        }} className="space-y-6" data-id="element-2462" data-mp="3tz8qm4zmu2ru">
            {/* Pathways Assignment */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-2463" data-mp="cl4osxn47r2a">
              <div className="p-5 border-b border-gray-200 bg-gray-50/50" data-id="element-2464" data-mp="3b3a9skc0nr04">
                <div className="flex items-center gap-2 mb-1" data-id="element-2465" data-mp="3g1bq4xi25c7w">
                  <Map size={18} className="text-[#30718D]" data-id="element-2466" data-mp="3g1tvqkqm12sh" />
                  <h2 className="text-lg font-bold text-gray-900" data-id="element-2467" data-mp="3kzlmvcjfkkvm">
                    Assign Pathways
                  </h2>
                </div>
                <p className="text-xs text-gray-500" data-id="element-2468" data-mp="3p05mcmrh80kx">
                  Select learning pathways for all imported users.
                </p>
              </div>

              <div className="p-5" data-id="element-2469" data-mp="14apx3w7wojxe">
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100" data-id="element-2470" data-mp="14e3xiyx0ej27">
                  <label htmlFor="toggle-all-pathways" className="text-sm font-semibold text-gray-900 cursor-pointer" data-id="element-2471" data-mp="2ovwezfd2p2ol">
                    Enable All Pathways
                  </label>
                  <button id="toggle-all-pathways" role="switch" aria-checked={enableAllPathways} onClick={() => setEnableAllPathways(!enableAllPathways)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2 ${enableAllPathways ? 'bg-[#30718D]' : 'bg-gray-200'}`} data-id="element-2472" data-mp="2fzx37hmo3z4z">
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enableAllPathways ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2473" data-mp="1x3owe9e0bg7k" />
                  </button>
                </div>

                <div className="space-y-3" data-id="element-2474" data-mp="3rg5atxgyi0ni">
                  {availablePathways.map(pathway => <label key={pathway} className="flex items-start gap-3 cursor-pointer group" data-id="element-2475" data-mp="3mj54dlg72vvv">
                      <div className="flex items-center h-5 mt-0.5" data-id="element-2476" data-mp="2vdspwtczmkn7">
                        <input type="checkbox" checked={selectedPathways.includes(pathway)} onChange={() => togglePathway(pathway)} className="w-4 h-4 rounded border-gray-300 text-[#30718D] focus:ring-[#30718D] transition-colors" data-id="element-2477" data-mp="30c2lh52mjn9c" />
                      </div>
                      <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors" data-id="element-2478" data-mp="2qjohe5o2q4k0">
                        {pathway}
                      </span>
                    </label>)}
                </div>
              </div>
            </div>

            {/* Skills Assignment */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-2479" data-mp="22pn8ogl57ds4">
              <div className="p-5 border-b border-gray-200 bg-gray-50/50" data-id="element-2480" data-mp="27ozrmq8w80pf">
                <div className="flex items-center gap-2 mb-1" data-id="element-2481" data-mp="xqut0bi5ru6">
                  <BookOpen size={18} className="text-[#30718D]" data-id="element-2482" data-mp="5w26lglcycf1" />
                  <h2 className="text-lg font-bold text-gray-900" data-id="element-2483" data-mp="5t67hoxrxhp0">
                    Assign Skills
                  </h2>
                </div>
                <p className="text-xs text-gray-500" data-id="element-2484" data-mp="1lf9jxmj1ts7p">
                  Select individual skills for all imported users.
                </p>
              </div>

              <div className="p-5" data-id="element-2485" data-mp="2057842gzahww">
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100" data-id="element-2486" data-mp="1vce45gtc3dlv">
                  <label htmlFor="toggle-all-skills" className="text-sm font-semibold text-gray-900 cursor-pointer" data-id="element-2487" data-mp="293157fmkhfl4">
                    Enable All Skills
                  </label>
                  <button id="toggle-all-skills" role="switch" aria-checked={enableAllSkills} onClick={() => setEnableAllSkills(!enableAllSkills)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2 ${enableAllSkills ? 'bg-[#30718D]' : 'bg-gray-200'}`} data-id="element-2488" data-mp="2j2s1nmuou4nu">
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enableAllSkills ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2489" data-mp="jr7gdesadykq" />
                  </button>
                </div>

                <div className="space-y-3 max-h-[240px] overflow-y-auto pr-2" data-id="element-2490" data-mp="2mk9gc2ytnnfv">
                  {availableSkills.map(skill => <label key={skill} className="flex items-start gap-3 cursor-pointer group" data-id="element-2491" data-mp="2hlg5kiax6aje">
                      <div className="flex items-center h-5 mt-0.5" data-id="element-2492" data-mp="1tvbz836cjwop">
                        <input type="checkbox" checked={selectedSkills.includes(skill)} onChange={() => toggleSkill(skill)} className="w-4 h-4 rounded border-gray-300 text-[#30718D] focus:ring-[#30718D] transition-colors" data-id="element-2493" data-mp="1txohyijxib2t" />
                      </div>
                      <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors" data-id="element-2494" data-mp="1yuf3vmif00w5">
                        {skill}
                      </span>
                    </label>)}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>;
}